import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, Wrench } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { useCustomSkillsStore } from "@/store/customSkillsStore";
import { useHasPermission } from "@/store/authStore";
import { toast } from "sonner";
import { CUSTOM_SKILL_STATUS, Permission } from "@forgepoint/shared";
import { CustomSkillEditorSheet } from "./CustomSkillEditorSheet";
import { CustomSkillSyncBanner } from "./CustomSkillSyncBanner";

/**
 * Custom skills settings panel listing skills with CRUD actions.
 * Uses the standard settings UX pattern with section labels, divide-y rows, and skeleton loading.
 * @returns Custom skills panel element
 */
export function CustomSkillsCard() {
  const skills = useCustomSkillsStore((s) => s.skills);
  const listLoading = useCustomSkillsStore((s) => s.listLoading);
  const listError = useCustomSkillsStore((s) => s.listError);
  const fetchSkills = useCustomSkillsStore((s) => s.fetchSkills);
  const deleteSkill = useCustomSkillsStore((s) => s.deleteSkill);
  const canEdit = useHasPermission(Permission.SETTINGS_EDIT);

  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);

  useEffect(() => {
    fetchSkills().then(setSkillIds);
  }, [fetchSkills]);

  /**
   * Opens the editor sheet in create mode.
   */
  const handleCreate = () => {
    setEditingSkillId(null);
    setEditorOpen(true);
  };

  /**
   * Opens the editor sheet for an existing skill.
   * @param skillId - ID of the skill to edit
   */
  const handleEdit = (skillId: string) => {
    setEditingSkillId(skillId);
    setEditorOpen(true);
  };

  /**
   * Deletes the skill currently staged for deletion.
   */
  const handleDelete = async () => {
    if (!deletingSkillId) {
      return;
    }

    const result = await deleteSkill(deletingSkillId);
    if (result.success) {
      toast.success("Skill deleted");
      setSkillIds((prev) => prev.filter((id) => id !== deletingSkillId));
    } else {
      toast.error(result.error ?? "Failed to delete skill");
    }
    setDeletingSkillId(null);
  };

  /**
   * Refreshes the skills list after a save operation.
   */
  const handleSaved = () => {
    fetchSkills().then(setSkillIds);
  };

  const deletingSkill = deletingSkillId
    ? skills.get(deletingSkillId)
    : undefined;

  // syncVersion is the ownership marker: non-null = code-managed (System-Managed),
  // null = created by a user in the app.
  const syncedIds = skillIds.filter((id) =>
    Boolean(skills.get(id)?.syncVersion),
  );
  const userIds = skillIds.filter((id) => !skills.get(id)?.syncVersion);

  const renderSkillRow = (id: string) => {
    const skill = skills.get(id);
    if (!skill) {
      return null;
    }
    return (
      <SkillRow
        key={id}
        name={skill.name}
        description={skill.description}
        status={skill.status}
        toolCount={skill.tools.length}
        syncVersion={skill.syncVersion}
        syncUpdatedBy={skill.syncUpdatedBy}
        updatedAt={skill.updatedAt}
        canEdit={canEdit}
        onEdit={() => handleEdit(id)}
        onDelete={() => setDeletingSkillId(id)}
      />
    );
  };

  if (listLoading) {
    return <CustomSkillsSkeleton />;
  }

  if (listError) {
    return (
      <div className="space-y-6 pt-4">
        <SectionLabel title="Custom Skills" />
        <Card className="border-destructive/40 shadow-sm">
          <CardContent className="py-6 text-sm text-destructive">
            {listError}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 pt-4">
        {/* ── Header ── */}
        <div
          id="custom-skills-header"
          className="flex items-center justify-between scroll-mt-24"
        >
          <SectionLabel title="Custom Skills" />
          {canEdit && (
            <Button type="button" size="sm" onClick={handleCreate}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Skill
            </Button>
          )}
        </div>

        {/* ── Skills List ── */}
        {skillIds.length === 0 ? (
          <Card className="shadow-sm overflow-hidden">
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
                <Wrench className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No custom skills yet</p>
              <p className="text-xs text-muted-foreground mb-4">
                Create a skill to give your agent additional capabilities.
              </p>
              {canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCreate}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Create your first skill
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Admin-Managed: skills created in the app — left untouched by sync */}
            {userIds.length > 0 && (
              <div className="space-y-2">
                {syncedIds.length > 0 && (
                  <div className="pl-2 space-y-0.5">
                    <h4 className="text-sm font-semibold">Admin-Managed</h4>
                    <p className="text-xs text-muted-foreground">
                      Created in the app by your team. These are not changed by
                      system updates.
                    </p>
                  </div>
                )}
                <Card className="shadow-sm overflow-hidden">
                  <div className="divide-y">{userIds.map(renderSkillRow)}</div>
                </Card>
              </div>
            )}

            {/* System-Managed: skills synced from code */}
            {syncedIds.length > 0 && (
              <div className="space-y-2">
                <div className="pl-2 space-y-0.5">
                  <h4 className="text-sm font-semibold">System-Managed</h4>
                  <p className="text-xs text-muted-foreground">
                    Configured automatically. Manual edits will be overwritten
                    on the next system update.
                  </p>
                </div>
                <Card className="shadow-sm overflow-hidden bg-muted/20">
                  <div className="divide-y">
                    {syncedIds.map(renderSkillRow)}
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>

      <CustomSkillEditorSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        skillId={editingSkillId}
        onSaved={handleSaved}
      />

      <DeleteConfirmationDialog
        open={deletingSkillId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingSkillId(null);
          }
        }}
        onConfirm={handleDelete}
        entityType="Skill"
        itemName={deletingSkill?.name}
      />
    </>
  );
}

// ── Local Components ──

interface SkillRowProps {
  /** Skill display name */
  name: string;
  /** Skill description text */
  description: string;
  /** Current skill status */
  status: string;
  /** Number of tools in the skill */
  toolCount: number;
  /** Git SHA of the last sync, or null if never synced */
  syncVersion: string | null;
  /** "sync" or the user id who last edited the skill */
  syncUpdatedBy: string | null;
  /** When the skill was last updated */
  updatedAt: Date;
  /** Whether the current user can edit or delete the skill */
  canEdit: boolean;
  /** Handler for editing the skill */
  onEdit: () => void;
  /** Handler for deleting the skill */
  onDelete: () => void;
}

/**
 * A single skill row in the divide-y list, following the SettingsRow layout pattern.
 * @param props - SkillRowProps
 * @returns Skill row element
 */
function SkillRow({
  name,
  description,
  status,
  toolCount,
  syncVersion,
  syncUpdatedBy,
  updatedAt,
  canEdit,
  onEdit,
  onDelete,
}: SkillRowProps) {
  return (
    <div className="group flex items-center justify-between gap-4 px-4 py-3.5 hover:bg-muted/30 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{name}</p>
          <Badge
            variant={
              status === CUSTOM_SKILL_STATUS.ACTIVE ? "default" : "secondary"
            }
            className="text-[10px] px-1.5 py-0"
          >
            {status === CUSTOM_SKILL_STATUS.ACTIVE ? "Active" : "Disabled"}
          </Badge>
          <span className="text-[11px] text-muted-foreground/60">
            {toolCount} {toolCount === 1 ? "tool" : "tools"}
          </span>
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5 max-w-md leading-snug">
            {description}
          </p>
        )}
        <div className="mt-1">
          <CustomSkillSyncBanner
            syncVersion={syncVersion}
            syncUpdatedBy={syncUpdatedBy}
            updatedAt={updatedAt}
          />
        </div>
      </div>
      {canEdit && (
        <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton loading state matching the CustomSkillsCard layout.
 * @returns Skeleton layout element
 */
function CustomSkillsSkeleton() {
  return (
    <div className="space-y-6 pt-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-28 ml-2" />
        <Skeleton className="h-8 w-24 rounded-md" />
      </div>
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonRow key={`skill-${i}`} />
          ))}
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the SkillRow layout.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-14 rounded-full" />
        </div>
        <Skeleton className="h-3 w-56" />
      </div>
      <div className="flex gap-1">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}
